import { useState, useEffect } from 'react';
import { useTranslation } from '../lib/i18n';
import { useAuthStore } from '../lib/auth';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Shield, ShieldCheck, ShieldOff, Smartphone, Eye, EyeOff, Copy, RefreshCw, Loader2, Fingerprint, ScanFace, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  isWebAuthnSupported,
  isPlatformAuthenticatorAvailable,
  registerBiometric,
} from '../lib/webauthn';

export function SecuritySettings() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [twoFAEnabled, setTwoFAEnabled] = useState(false);
  const [setupOpen, setSetupOpen] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [setupStep, setSetupStep] = useState(1);
  const [verifyCode, setVerifyCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [disableOpen, setDisableOpen] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [disabling, setDisabling] = useState(false);
  const [showDisablePass, setShowDisablePass] = useState(false);
  const [backupOpen, setBackupOpen] = useState(false);
  const [backupCodes, setBackupCodes] = useState([]);
  const [regenerating, setRegenerating] = useState(false);

  // Biometric state
  const [biometricSupported, setBiometricSupported] = useState(false);
  const [biometricCredentials, setBiometricCredentials] = useState([]);
  const [biometricLoading, setBiometricLoading] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchStatus(); checkBiometric(); }, []);

  const fetchStatus = async () => {
    try {
      const { data } = await api.get('/2fa/status');
      setTwoFAEnabled(data.enabled);
    } catch { /* ignore */ }
    setLoading(false);
  };

  const startSetup = async () => {
    try {
      const { data } = await api.post('/2fa/setup');
      setSetupData(data);
      setSetupStep(1);
      setVerifyCode('');
      setSetupOpen(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const verifySetup = async () => {
    setVerifying(true);
    try {
      await api.post('/2fa/verify-setup', { code: verifyCode });
      setTwoFAEnabled(true);
      setSetupStep(3);
      toast.success(t('auth.twoFAEnabled'));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid code');
    }
    setVerifying(false);
  };

  const disable2FA = async () => {
    setDisabling(true);
    try {
      await api.post('/2fa/disable', { password: disablePassword });
      setTwoFAEnabled(false);
      setDisableOpen(false);
      setDisablePassword('');
      toast.success(t('auth.twoFADisabled'));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
    setDisabling(false);
  };

  const fetchBackupCodes = async () => {
    try {
      const { data } = await api.get('/2fa/backup-codes');
      setBackupCodes(Array.isArray(data) ? data : (data?.backup_codes || []));
      setBackupOpen(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const regenerateCodes = async () => {
    setRegenerating(true);
    try {
      const { data } = await api.post('/2fa/regenerate-backup-codes');
      setBackupCodes(Array.isArray(data) ? data : (data?.backup_codes || []));
      toast.success(t('auth.twoFABackupCodes'));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
    setRegenerating(false);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success(t('auth.twoFACopied'));
  };

  const checkBiometric = async () => {
    const supported = isWebAuthnSupported() && await isPlatformAuthenticatorAvailable();
    setBiometricSupported(supported);
    if (supported) fetchBiometricCredentials();
  };

  const fetchBiometricCredentials = async () => {
    try {
      const res = await api.get('/auth/webauthn/credentials');
      if (res.data.success) setBiometricCredentials(res.data.credentials || []);
    } catch {}
  };

  const handleRegisterBiometric = async () => {
    setBiometricLoading(true);
    try {
      const token = useAuthStore.getState().accessToken;
      await registerBiometric(token);
      toast.success(t('lockScreen.biometricRegistered'));
      localStorage.removeItem('oms_biometric_dismissed');
      fetchBiometricCredentials();
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        toast.error(t('lockScreen.biometricCancelled'));
      } else {
        toast.error(err.message || t('lockScreen.biometricFailed'));
      }
    } finally {
      setBiometricLoading(false);
    }
  };

  const handleDeleteBiometric = async (credentialId) => {
    try {
      await api.delete(`/auth/webauthn/credentials/${credentialId}`);
      toast.success(t('lockScreen.biometricDeleted'));
      fetchBiometricCredentials();
    } catch {
      toast.error(t('lockScreen.biometricFailed'));
    }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-6">
      <Card data-testid="2fa-settings-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-cyan-600" />
            {t('auth.twoFA')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 rounded-lg bg-slate-50 dark:bg-slate-800/50">
            <div className="flex items-center gap-3">
              {twoFAEnabled ? (
                <ShieldCheck className="h-8 w-8 text-green-500" />
              ) : (
                <ShieldOff className="h-8 w-8 text-slate-400" />
              )}
              <div>
                <p className="font-medium">
                  {twoFAEnabled ? t('auth.twoFAEnabled') : t('auth.twoFADisabled')}
                </p>
                <p className="text-sm text-slate-500">
                  {twoFAEnabled ? t('auth.twoFAProtected') : t('auth.twoFAEncourage')}
                </p>
              </div>
            </div>
            <Badge variant={twoFAEnabled ? 'default' : 'secondary'} className={twoFAEnabled ? 'bg-green-100 text-green-700' : ''}>
              {twoFAEnabled ? t('auth.twoFAActive') : t('auth.twoFAInactive')}
            </Badge>
          </div>

          <div className="flex items-center gap-2 text-xs text-slate-500">
            <Smartphone className="h-3.5 w-3.5" />
            <span>{t('auth.twoFASupportedApps')}</span>
          </div>

          <div className="flex gap-3">
            {!twoFAEnabled ? (
              <Button onClick={startSetup} className="bg-cyan-600 hover:bg-cyan-700" data-testid="enable-2fa-btn">
                <ShieldCheck className="h-4 w-4 mr-2" />
                {t('auth.twoFAEnable')}
              </Button>
            ) : (
              <>
                <Button variant="outline" onClick={fetchBackupCodes} data-testid="view-backup-codes-btn">
                  <Eye className="h-4 w-4 mr-2" />
                  {t('auth.twoFABackupCodes')}
                </Button>
                <Button variant="destructive" onClick={() => setDisableOpen(true)} data-testid="disable-2fa-btn">
                  <ShieldOff className="h-4 w-4 mr-2" />
                  {t('auth.twoFADisable')}
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Biometric Card */}
      {biometricSupported && (
        <Card data-testid="biometric-settings-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Fingerprint className="h-5 w-5 text-emerald-600" />
              {t('lockScreen.biometricSecurity')}
            </CardTitle>
            <CardDescription>
              {t('lockScreen.biometricDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {biometricCredentials.length > 0 && (
              <div className="space-y-2">
                {biometricCredentials.map((cred) => (
                  <div key={cred.credential_id} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <ScanFace className="h-5 w-5 text-emerald-500" />
                      <div>
                        <p className="text-sm font-medium">{cred.device_name || t('lockScreen.biometricSecurity')}</p>
                        <p className="text-xs text-slate-400">
                          {cred.last_used_at ? `${t('lockScreen.lastUsed')}: ${new Date(cred.last_used_at).toLocaleDateString()}` : cred.created_at ? `${t('lockScreen.registered')}: ${new Date(cred.created_at).toLocaleDateString()}` : ''}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteBiometric(cred.credential_id)}
                      className="text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                      data-testid={`delete-biometric-${cred.credential_id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-center gap-3">
              <Button
                onClick={handleRegisterBiometric}
                disabled={biometricLoading}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="register-biometric-btn"
              >
                {biometricLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Fingerprint className="h-4 w-4 mr-2" />
                )}
                {biometricCredentials.length > 0 
                  ? t('lockScreen.addAnotherDevice')
                  : t('lockScreen.registerBiometric')
                }
              </Button>
              {biometricCredentials.length > 0 && (
                <Badge variant="default" className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                  {biometricCredentials.length} {t('lockScreen.registeredDevices')}
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Setup Dialog */}
      <Dialog open={setupOpen} onOpenChange={(open) => { if (!open && setupStep !== 2) setSetupOpen(false); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-cyan-600" />
              {setupStep === 3 ? t('auth.twoFAYourBackupCodes') : t('auth.twoFASetup')}
            </DialogTitle>
          </DialogHeader>

          {setupStep === 1 && setupData && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {t('auth.twoFAScanQR')}
              </p>
              <div className="flex justify-center p-4 bg-white rounded-xl">
                <img src={`data:image/png;base64,${setupData.qr_code}`} alt="QR Code" className="w-48 h-48" data-testid="2fa-qr-code" />
              </div>
              <details className="text-xs">
                <summary className="cursor-pointer text-slate-500">{t('auth.twoFACantScanQR')}</summary>
                <div className="mt-2 p-2 bg-slate-50 dark:bg-slate-800 rounded font-mono text-xs break-all select-all" data-testid="2fa-secret-display">
                  {setupData.secret}
                </div>
              </details>
              <Button className="w-full" onClick={() => setSetupStep(2)} data-testid="2fa-setup-next-btn">
                {t('auth.twoFAScannedNext')}
              </Button>
            </div>
          )}

          {setupStep === 2 && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {t('auth.twoFAEnterCode')}
              </p>
              <Input
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder="000000"
                value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ''))}
                onKeyDown={(e) => e.key === 'Enter' && verifyCode.length === 6 && verifySetup()}
                className="text-center text-2xl font-mono tracking-[0.5em] h-14"
                data-testid="2fa-verify-input"
              />
              <Button className="w-full bg-cyan-600 hover:bg-cyan-700" onClick={verifySetup} disabled={verifyCode.length !== 6 || verifying} data-testid="2fa-verify-submit">
                {verifying ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {t('auth.twoFAVerifyAndEnable')}
              </Button>
            </div>
          )}

          {setupStep === 3 && setupData && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {t('auth.twoFASaveBackupCodes')}
              </p>
              <div className="grid grid-cols-2 gap-2 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg" data-testid="backup-codes-grid">
                {setupData.backup_codes.map((code, i) => (
                  <code key={i} className="text-sm font-mono text-center py-1 bg-white dark:bg-slate-700 rounded">{code}</code>
                ))}
              </div>
              <Button variant="outline" className="w-full" onClick={() => copyToClipboard(setupData.backup_codes.join('\n'))}>
                <Copy className="h-4 w-4 mr-2" /> {t('auth.twoFACopyCodes')}
              </Button>
              <Button className="w-full" onClick={() => setSetupOpen(false)} data-testid="2fa-setup-done-btn">
                {t('auth.twoFAComplete')}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Disable Dialog */}
      <Dialog open={disableOpen} onOpenChange={setDisableOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-red-600">{t('auth.twoFADisable')}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            {t('auth.twoFADisableConfirm')}
          </p>
          <div className="relative">
            <Input
              type={showDisablePass ? 'text' : 'password'}
              placeholder={t('auth.twoFAYourPassword')}
              value={disablePassword}
              onChange={(e) => setDisablePassword(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && disable2FA()}
              data-testid="disable-2fa-password"
            />
            <button type="button" onClick={() => setShowDisablePass(!showDisablePass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
              {showDisablePass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDisableOpen(false)}>{t('auth.twoFACancel')}</Button>
            <Button variant="destructive" onClick={disable2FA} disabled={!disablePassword || disabling} data-testid="disable-2fa-confirm">
              {disabling ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {t('auth.twoFADisable')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Backup Codes Dialog */}
      <Dialog open={backupOpen} onOpenChange={setBackupOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('auth.twoFABackupCodes')}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-2 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg" data-testid="backup-codes-list">
            {backupCodes.map((code, i) => (
              <code key={i} className="text-sm font-mono text-center py-1 bg-white dark:bg-slate-700 rounded">{code}</code>
            ))}
          </div>
          <p className="text-xs text-slate-500">{t('auth.twoFARemainingCodes').replace('{count}', backupCodes.length)}</p>
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={() => copyToClipboard(backupCodes.join('\n'))}>
              <Copy className="h-4 w-4 mr-2" /> {t('auth.twoFACopy')}
            </Button>
            <Button variant="outline" className="flex-1" onClick={regenerateCodes} disabled={regenerating}>
              {regenerating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              {t('auth.twoFARegenerate')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
